import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import {
  CssBaseline,
  Dialog,
  DialogContent,
  ThemeProvider,
} from '@material-ui/core';
import { useEffect } from 'react';
import { useClearCache } from 'react-clear-cache';
import { hotjar } from 'react-hotjar';
import { BrowserRouter as Router, Routes } from 'react-router-dom';

import { registerDomain } from 'data-layer-cr/dist/authentication';
import { NetworkIndicator } from 'design-system';

import applicationDomain from './functions/applicationDomain';
import routes from './routing';
import { persistor } from './store';
import modifiedTheme from './theme/modifiedTheme';

export const cacheCheckDuration = 1000 * 60 * 5;
export const storageKey = 'AstroKong-Inspection-Version';

const { REACT_APP_HOTJAR_KEY } = process.env;

const growthbook = new GrowthBook({
  apiHost: process.env.REACT_APP_GROWTHBOOK_API_HOST,
  clientKey: process.env.REACT_APP_GROWTHBOOK_CLIENT_KEY,
});

function App(): JSX.Element {
  /**
   * This is the cache clearing logic defined for the inspection section. Note that it will automatically
   * refresh the page when the timeout expires, since we must ensure that the client always see the latest
   * version of our application, and long sessions are not as common here as on the sales-manager site.
   */
  const { isLatestVersion, emptyCacheStorage } = useClearCache({
    /** 5 minutes before the check is done */
    duration: cacheCheckDuration,
    auto: false,
    storageKey,
    basePath: '',
  });

  useEffect(() => {
    if (!isLatestVersion) {
      setTimeout(() => {
        persistor.purge();
        emptyCacheStorage();
      }, 3500);
    }
  }, [isLatestVersion, emptyCacheStorage]);

  useEffect(() => {
    hotjar.initialize(Number(REACT_APP_HOTJAR_KEY), 6);
    growthbook.loadFeatures({ autoRefresh: true });
  }, []);

  /**
   * Registers the app's base url as the domain for the session data cookies.
   * This allows us to share the session between projects
   */
  const appUrl = applicationDomain(window.location.hostname);
  useEffect(() => {
    registerDomain(appUrl);
  }, [appUrl]);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <ThemeProvider theme={modifiedTheme}>
        <CssBaseline />
        <Router>
          <Routes>{routes.map((route) => route)}</Routes>
        </Router>

        {/* Automatic app update */}
        <Dialog data-testid="update-app-modal" open={!isLatestVersion}>
          <DialogContent style={{ width: 295, padding: '31.5px 20px' }}>
            <NetworkIndicator requesting message="Actualizando..." />
          </DialogContent>
        </Dialog>
      </ThemeProvider>
    </GrowthBookProvider>
  );
}

export default App;
